// Apply Edge landing — port of design/source/public/index.html.
// Sections, in order, mirror the design's `<LandingPage>` composition:
//   Hero → LogoCloud → FeatureGrid → HowItWorks → FAQ → CTA
// Chrome (NavBar + Footer) is rendered by HomeLayout — do NOT add them here.
import { t, Trans } from "@lingui/macro";
import { ArrowRightIcon, PlayIcon, PlusIcon } from "@phosphor-icons/react";
import { getPlan } from "@reactive-resume/utils";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";

import { SEO } from "@/client/components/seo";

import { CompareSection } from "./home-compare";
import { FAQ_SCHEMA } from "./home-faq-schema";

// ──────────────────────────────────────────────────────────────────────────
// Building blocks ported from index.html
// ──────────────────────────────────────────────────────────────────────────

type GaugeProps = { value: number; size?: number };

const Gauge = ({ value, size = 112 }: GaugeProps) => {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (circumference * value) / 100;
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg viewBox="0 0 80 80" className="size-full -rotate-90">
        <circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth="4"
        />
        <circle
          cx="40"
          cy="40"
          r={radius}
          fill="none"
          stroke="hsl(var(--accent))"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 800ms cubic-bezier(.22,.9,.34,1)" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="font-mono text-[26px] font-semibold leading-none num-tabular">
            {value}
          </div>
          <div className="mt-1 text-xs font-medium text-muted-foreground">
            <Trans>ATS</Trans>
          </div>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Tailoring strip — pills animate in on scroll
// ──────────────────────────────────────────────────────────────────────────

const getTailoringKeywords = () =>
  [
    t`Kubernetes`,
    t`Terraform`,
    t`SRE`,
    t`on-call rotation`,
    t`observability`,
    t`Prometheus`,
    t`incident response`,
    t`mentored 4 engineers`,
    t`reduced p95 42%`,
  ] as const;

const TailoringStrip = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();

        let i = 0;
        const tick = () => {
          i++;
          setVisibleCount(i);
          if (i < getTailoringKeywords().length) {
            setTimeout(tick, 180);
          }
        };
        setTimeout(tick, 400);
      },
      { threshold: 0.5 },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      className="mt-4 rounded-2xl border border-border bg-surface/70 px-4 py-3 backdrop-blur"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <span className="relative flex size-2">
            <span className="absolute inset-0 animate-ping rounded-full bg-accent opacity-60" />
            <span className="relative m-auto size-1.5 rounded-full bg-accent" />
          </span>
          <Trans>Tailoring</Trans>
        </div>
        <div className="font-mono text-[11px] text-muted-foreground">
          <span className="text-foreground">{Math.min(visibleCount, 3)}</span>{" "}
          <Trans>/ 9 matched</Trans>
        </div>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {getTailoringKeywords().map((kw, i) => (
          <span
            key={kw}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] transition-all duration-500 ${
              i >= visibleCount ? "translate-y-2 border-transparent opacity-0" : ""
            } ${
              i < visibleCount && i < 3
                ? "translate-y-0 border-accent/30 bg-accent-soft text-accent opacity-100"
                : ""
            } ${
              i < visibleCount && i >= 3
                ? "translate-y-0 border-border bg-muted text-muted-foreground opacity-60"
                : ""
            }`}
          >
            <span
              className={`inline-block size-1 rounded-full ${
                i >= visibleCount ? "bg-transparent" : ""
              } ${i < visibleCount && i < 3 ? "bg-accent" : ""} ${
                i < visibleCount && i >= 3 ? "bg-muted-foreground" : ""
              }`}
            />
            {kw}
          </span>
        ))}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Hero section
// ──────────────────────────────────────────────────────────────────────────

const HeroIllustration = () => (
  <div className="relative">
    <div className="pointer-events-none absolute -inset-10 -z-10 opacity-60 dot-grid mask-fade-b" />
    <div className="grid gap-6 sm:grid-cols-[1fr_1fr]">
      {/* Job description card */}
      <div className="relative h-full overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-medium text-muted-foreground">
              <Trans>Job description</Trans>
            </div>
            <div className="mt-1 text-[14px] font-semibold">
              <Trans>Senior Platform Engineer</Trans>
            </div>
            <div className="text-[11.5px] text-muted-foreground">
              <Trans>Vercel · Remote · $180k–$220k</Trans>
            </div>
          </div>
          <div className="rounded-full border border-border px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
            <Trans>Public job board</Trans>
          </div>
        </div>
        <div className="mt-4 space-y-1.5 text-[11.5px] leading-relaxed text-foreground/75">
          <p>
            <Trans>We&apos;re looking for a platform engineer to own our</Trans>{" "}
            <mark className="rounded-sm bg-accent/15 px-1 text-accent">
              <Trans>Kubernetes</Trans>
            </mark>{" "}
            +{" "}
            <mark className="rounded-sm bg-accent/15 px-1 text-accent">
              <Trans>Terraform</Trans>
            </mark>{" "}
            <Trans>footprint.</Trans>
          </p>
          <p>
            <Trans>You&apos;ll lead the</Trans>{" "}
            <mark className="rounded-sm bg-accent/15 px-1 text-accent">
              <Trans>SRE</Trans>
            </mark>{" "}
            <Trans>practice, own</Trans>{" "}
            <mark className="rounded-sm bg-accent/15 px-1 text-accent">
              <Trans>on-call rotation</Trans>
            </mark>
            <Trans>, and push our</Trans>{" "}
            <mark className="rounded-sm bg-accent/15 px-1 text-accent">
              <Trans>observability</Trans>
            </mark>{" "}
            (
            <mark className="rounded-sm bg-accent/15 px-1 text-accent">
              <Trans>Prometheus</Trans>
            </mark>
            <Trans>, Grafana) forward.</Trans>
          </p>
          <p>
            <Trans>Background in</Trans>{" "}
            <mark className="rounded-sm bg-accent/15 px-1 text-accent">
              <Trans>incident response</Trans>
            </mark>{" "}
            <Trans>
              and mentoring — you&apos;ve shipped through real outages and made teams faster.
            </Trans>
          </p>
        </div>
        <div className="mt-4 flex items-center gap-2 text-[11px] text-muted-foreground">
          <span className="inline-block size-1.5 rounded-full bg-accent" />
          <span className="font-mono">
            <Trans>9 matched keywords</Trans>
          </span>
        </div>
      </div>

      {/* Resume card */}
      <div className="relative h-full overflow-hidden rounded-2xl border border-border bg-surface p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-medium text-muted-foreground">
              <Trans>Your resume — tailored</Trans>
            </div>
            <div className="mt-1 text-[14px] font-semibold">
              <Trans>Ada Okafor</Trans>
            </div>
            <div className="text-[11.5px] text-muted-foreground">
              <Trans>Platform Engineer · 7 yrs</Trans>
            </div>
          </div>
          <Gauge value={96} />
        </div>
        <div className="mt-4 space-y-2 text-[11.5px] leading-relaxed">
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              <Trans>Experience — Staff Engineer, Plaid</Trans>
            </div>
            <ul className="space-y-1 text-foreground/80">
              <li className="flex gap-2">
                <span className="mt-1 size-1 shrink-0 rounded-full bg-accent" />
                <span>
                  <Trans>Led</Trans>{" "}
                  <u className="decoration-accent decoration-2 underline-offset-2">
                    <Trans>SRE</Trans>
                  </u>{" "}
                  <Trans>across 12 services;</Trans>{" "}
                  <u className="decoration-accent decoration-2 underline-offset-2">
                    <Trans>reduced p95 42%</Trans>
                  </u>{" "}
                  <Trans>in one quarter.</Trans>
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1 size-1 shrink-0 rounded-full bg-accent" />
                <span>
                  <Trans>Owned</Trans>{" "}
                  <u className="decoration-accent decoration-2 underline-offset-2">
                    <Trans>Kubernetes</Trans>
                  </u>{" "}
                  +{" "}
                  <u className="decoration-accent decoration-2 underline-offset-2">
                    <Trans>Terraform</Trans>
                  </u>{" "}
                  <Trans>platform; migrated 40 services with zero downtime.</Trans>
                </span>
              </li>
              <li className="flex gap-2">
                <span className="mt-1 size-1 shrink-0 rounded-full bg-accent" />
                <span>
                  <Trans>Ran</Trans>{" "}
                  <u className="decoration-accent decoration-2 underline-offset-2">
                    <Trans>on-call rotation</Trans>
                  </u>{" "}
                  <Trans>for 22 engineers; cut</Trans>{" "}
                  <u className="decoration-accent decoration-2 underline-offset-2">
                    <Trans>incident response</Trans>
                  </u>{" "}
                  <Trans>MTTR by 60%.</Trans>
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 border-t border-border pt-3 text-[11px]">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-soft px-2 py-0.5 font-mono text-[10px] text-accent">
            <span className="size-1.5 rounded-full bg-accent" /> <Trans>tailored · 4s</Trans>
          </span>
          <span className="text-muted-foreground">
            <Trans>Ready for you to review and send</Trans>
          </span>
        </div>
      </div>
    </div>

    <TailoringStrip />
  </div>
);

const Hero = () => (
  <section className="relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0 opacity-40 fine-grid mask-fade-b" />
    <div className="mx-auto grid max-w-[1280px] items-center gap-12 px-6 pb-16 pt-12 md:grid-cols-[1fr_1.1fr] md:pt-20">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-2.5 py-1 font-mono text-[11px] text-muted-foreground">
          <span className="size-1.5 rounded-full bg-accent" />
          <span>
            <Trans>New:</Trans>{" "}
            <span className="text-foreground">
              <Trans>Inbox signals</Trans>
            </span>{" "}
            <Trans>· surface recruiter replies as they land</Trans>
          </span>
        </div>
        <h1 className="mt-6 text-balance text-[44px] font-semibold leading-[1.1] tracking-[-0.02em] duration-700 animate-in fade-in slide-in-from-bottom-4 sm:text-[52px] md:text-[68px]">
          <Trans>Write it once.</Trans>
          <br />
          <span className="font-serif font-normal italic">
            <Trans>Send it perfectly.</Trans>
          </span>
        </h1>
        <p className="mt-6 max-w-[46ch] text-pretty text-[16px] leading-[1.6] text-muted-foreground delay-75 duration-700 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
          <Trans>
            Apply Edge writes your resume, tailors each draft to the role you&apos;re eyeing, and
            pre-fills applications across the major job boards — so every submission is one review
            and one click, never copy-paste.
          </Trans>
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3 delay-150 duration-700 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
          <a
            href="/auth/register"
            className="group inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-[14px] font-medium text-primary-foreground shadow-sm transition duration-150 ease-out hover:-translate-y-[1px] hover:bg-primary/90 hover:shadow-md"
          >
            <Trans>Start free — build a resume</Trans>
            <ArrowRightIcon
              size={15}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </a>
          {/* Co-primary CTA into the free ATS checker (spec 035, FR-009). */}
          <a
            href="/tools/ats-checker"
            className="group inline-flex items-center gap-2 rounded-full border border-border bg-transparent px-5 py-3 text-[14px] font-medium text-foreground transition duration-150 ease-out hover:-translate-y-[1px] hover:bg-muted hover:shadow-sm"
          >
            <Trans>Try the free ATS checker</Trans>
            <ArrowRightIcon
              size={14}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </a>
          <a
            href="/help"
            className="group inline-flex items-center gap-2 rounded-full bg-transparent px-3 py-3 text-[14px] font-medium text-muted-foreground transition duration-150 ease-out hover:text-foreground"
          >
            <PlayIcon size={13} weight="fill" />
            <Trans>90-sec product tour</Trans>
          </a>
        </div>
        <div className="mt-8 text-[12px] text-muted-foreground delay-300 duration-700 animate-in fade-in slide-in-from-bottom-4 fill-mode-both">
          <Trans>Built by job seekers, for job seekers.</Trans>
        </div>
      </div>
      <HeroIllustration />
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
// Logo cloud placeholder
// ──────────────────────────────────────────────────────────────────────────

const LogoCloud = () => (
  <section className="border-y border-border bg-muted/40">
    <div className="mx-auto max-w-[1280px] px-6 py-6">
      <div className="text-xs font-medium text-muted-foreground">
        <Trans>Built by job seekers, for job seekers — no logos to brag about yet.</Trans>
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
// Feature grid (bento) + 4-stat strip
// ──────────────────────────────────────────────────────────────────────────

const Stat = ({ value, label }: { value: string; label: string }) => (
  <div>
    <div className="font-mono text-[28px] font-semibold tracking-tight num-tabular">{value}</div>
    <div className="mt-1 text-[12px] text-muted-foreground">{label}</div>
  </div>
);

type AtsTagBucket = readonly [label: string, tags: readonly string[]];

const getAtsBuckets = (): readonly AtsTagBucket[] => [
  [t`Covered`, [t`Kubernetes`, t`Terraform`, t`SRE`, t`Prometheus`]],
  [t`Missing`, [t`service mesh`, t`runbooks`]],
  [t`Bonus`, [t`zero-downtime`, t`p95`, t`MTTR`]],
];

type DraftQueueRow = readonly [time: string, job: string, state: "ready" | "tailoring" | "queued"];

const getDraftQueue = (): readonly DraftQueueRow[] => [
  ["03:11", t`Senior SWE · Linear`, "ready"],
  ["03:14", t`Staff Eng · Stripe`, "ready"],
  ["03:17", t`Platform · Vercel`, "tailoring"],
  ["03:19", t`DevEx · Shopify`, "queued"],
];

type ChromeRow = readonly [title: string, company: string, score: number];

const getChromeRows = (): readonly ChromeRow[] => [
  [t`Senior Platform Engineer`, t`Vercel`, 94],
  [t`Staff SRE`, t`Plaid`, 88],
  [t`Infra Engineer`, t`Ramp`, 71],
  [t`Sr. Backend`, t`Discord`, 54],
];

type KanbanColumn = readonly [head: string, count: number, style: number];

const getKanban = (): readonly KanbanColumn[] => [
  [t`Queued`, 12, 0],
  [t`Applied`, 38, 1],
  [t`Interview`, 6, 2],
  [t`Offer`, 1, 3],
];

const FeatureGrid = () => (
  <section className="bg-muted/30">
    <div className="mx-auto max-w-[1280px] px-6 py-24">
      <div className="max-w-[52ch]">
        <div className="text-xs font-medium text-muted-foreground">
          <Trans>Everything included</Trans>
        </div>
        <h2 className="mt-3 text-balance text-[32px] font-semibold leading-[1.05] tracking-[-0.02em] sm:text-[40px]">
          <Trans>A full application stack — not just another resume builder.</Trans>
        </h2>
      </div>

      <div className="mt-12 grid gap-4 md:grid-cols-6">
        {/* ATS Match Score — large card */}
        <Link
          to="/tools/ats-checker"
          className="relative col-span-6 overflow-hidden rounded-2xl border border-border bg-surface p-8 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md md:col-span-4"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-medium text-muted-foreground">
                <Trans>ATS Match Score</Trans>
              </div>
              <div className="mt-2 text-[22px] font-semibold tracking-tight">
                <Trans>See exactly why you&apos;re getting ghosted.</Trans>
              </div>
              <p className="mt-2 max-w-[48ch] text-[13px] text-muted-foreground">
                <Trans>
                  Paste any job description. We show your match score, every missing keyword, and
                  rewrite your bullets to cover them — without sounding like a bot.
                </Trans>
              </p>
            </div>
            <div className="hidden md:block">
              <Gauge value={91} />
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-2 text-[11px]">
            {getAtsBuckets().map(([head, tags]) => (
              <div key={head} className="rounded-xl border border-border bg-background p-3">
                <div className="text-xs font-medium text-muted-foreground">{head}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className={`rounded-full px-2 py-0.5 font-mono text-[10px] ${
                        head === "Missing"
                          ? "bg-warning-soft text-warning-strong"
                          : "bg-accent-soft text-accent"
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Link>

        {/* Draft queue — indigo card */}
        <Link
          to="/auth/register"
          className="relative col-span-6 overflow-hidden rounded-2xl border border-indigo-700 bg-indigo-700 p-6 text-white md:col-span-2"
        >
          <div className="text-xs font-medium text-white/70">
            <Trans>Draft queue</Trans>
          </div>
          <div className="mt-2 text-balance text-[22px] font-semibold tracking-tight">
            <Trans>Your evening queue — drafted, tailored, ready to review.</Trans>
          </div>
          <div className="mt-6 space-y-2 font-mono text-[11.5px]">
            {getDraftQueue().map(([time, job, state]) => (
              <div key={`${time}-${job}`} className="flex items-center gap-3">
                {/* This card is a fixed indigo-700 surface in BOTH themes, so these
                    use palette values rather than tokens — `text-accent` resolved
                    to emerald-700 here and measured 1.41:1. Alpha is set on the
                    text colour, not the element: `opacity-50` dimmed the background
                    too and measured 3.12:1. text-white/75 is 5.07:1. */}
                <span className="text-white/75">{time}</span>
                <span className="flex-1 truncate">{job}</span>
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] ${
                    state === "ready" ? "bg-emerald-400/20 text-emerald-50" : ""
                  } ${state === "tailoring" ? "bg-white/15" : ""} ${
                    state === "queued" ? "bg-white/10 text-white/90" : ""
                  }`}
                >
                  {state === "ready" && <Trans>Ready</Trans>}
                  {state === "tailoring" && <Trans>Tailoring</Trans>}
                  {state === "queued" && <Trans>Queued</Trans>}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 text-[11px]">
            <span className="relative inline-flex size-1.5">
              <span className="absolute inset-0 animate-pulse-ring rounded-full bg-accent" />
              <span className="relative inline-flex size-1.5 rounded-full bg-accent" />
            </span>
            <Trans>38 drafts ready · review &amp; send</Trans>
          </div>
        </Link>

        {/* Chrome extension preview */}
        <Link
          to="/auth/register"
          className="relative col-span-6 overflow-hidden rounded-2xl border border-border bg-surface p-6 md:col-span-3"
        >
          <div className="text-xs font-medium text-muted-foreground">
            <Trans>Chrome extension</Trans>
          </div>
          <div className="mt-2 text-[20px] font-semibold tracking-tight">
            <Trans>See your score next to every listing.</Trans>
          </div>
          <div className="mt-5 rounded-xl border border-border bg-background p-3">
            {getChromeRows().map(([title, company, score]) => (
              <div
                key={title}
                className="flex items-center justify-between gap-3 border-b border-border py-2 last:border-0"
              >
                <div>
                  <div className="text-[12.5px] font-medium">{title}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {company} <Trans>· Remote</Trans>
                  </div>
                </div>
                <div
                  className={`flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10.5px] ${
                    score >= 85 ? "bg-accent-soft text-accent" : ""
                  } ${score >= 70 && score < 85 ? "bg-info-soft text-info-strong" : ""} ${
                    score < 70 ? "bg-muted text-muted-foreground" : ""
                  }`}
                >
                  <span className="size-1 rounded-full bg-current" /> {score}
                </div>
              </div>
            ))}
          </div>
        </Link>

        {/* Mini Kanban */}
        <Link
          to="/applications"
          className="relative col-span-6 overflow-hidden rounded-2xl border border-border bg-surface p-6 md:col-span-3"
        >
          <div className="text-xs font-medium text-muted-foreground">
            <Trans>Application tracker</Trans>
          </div>
          <div className="mt-2 text-[20px] font-semibold tracking-tight">
            <Trans>Kanban that updates itself.</Trans>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-2 text-[10.5px] sm:grid-cols-4">
            {getKanban().map(([head, count, style]) => (
              <div key={head} className="rounded-xl border border-border bg-background p-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">{head}</span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 font-mono ${style === 0 ? "bg-muted text-muted-foreground" : ""} ${style === 1 ? "bg-info-soft text-info-strong" : ""} ${style === 2 ? "bg-accent-soft text-accent" : ""} ${style === 3 ? "bg-warning-soft text-warning-strong" : ""}`}
                  >
                    {count}
                  </span>
                </div>
                <div className="mt-2 space-y-1">
                  {Array.from({ length: Math.min(count, 3) }).map((_, idx) => (
                    <div
                      key={idx}
                      className="h-4 rounded border border-border bg-muted/40"
                      style={{ opacity: 1 - idx * 0.25 }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-[11.5px] text-muted-foreground">
            <Trans>
              Your inbox connection watches for recruiter replies and moves cards for you.
            </Trans>
          </div>
        </Link>
      </div>

      {/* Product capability strip */}
      <div className="mt-6 grid grid-cols-2 gap-x-10 gap-y-5 rounded-2xl border border-border bg-surface p-6 md:grid-cols-4">
        <Stat value={t`ATS`} label={t`Resume scoring`} />
        <Stat value={t`Queue`} label={t`Review before sending`} />
        <Stat value={t`Profiles`} label={t`Role-specific targeting`} />
        <Stat value={t`Tracker`} label={t`Application follow-up`} />
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
// How it works — 4 steps
// ──────────────────────────────────────────────────────────────────────────

const getHowSteps = () =>
  [
    {
      k: "01",
      chip: t`Resume builder`,
      t: t`Build your base`,
      d: t`Pick a template, drag sections into place. Your resume renders live in A4 or Letter.`,
    },
    {
      k: "02",
      chip: t`Job profiles`,
      t: t`Save a job profile`,
      d: t`Target roles, locations, salary, skills. We discover jobs & score them 0–100 against you.`,
    },
    {
      k: "03",
      chip: t`Tailoring`,
      t: t`AI tailors each resume`,
      d: t`Every application gets its own tailored resume. Review in the queue, or turn on auto-approve (beta).`,
    },
    {
      k: "04",
      chip: t`Tracker`,
      t: t`Review, send, track.`,
      d: t`Tailored drafts are ready in your queue. You review and submit in one click — we keep your Kanban honest from there.`,
    },
  ] as const;

const HowItWorks = () => (
  <section className="relative">
    <div className="mx-auto max-w-[1280px] px-6 py-24">
      <div className="flex items-end justify-between gap-6">
        <div>
          <div className="text-xs font-medium text-muted-foreground">
            <Trans>How it works</Trans>
          </div>
          <h2 className="mt-3 max-w-[20ch] text-balance text-[32px] font-semibold leading-[1.05] tracking-[-0.02em] sm:text-[40px]">
            <Trans>From a blank resume to applications that fit — in one evening.</Trans>
          </h2>
        </div>
        <div className="hidden max-w-[34ch] text-[13.5px] text-muted-foreground md:block">
          <Trans>
            Four steps: find roles that fit, tailor each resume, review the queue, and read the
            replies. You review and send with one click.
          </Trans>
        </div>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-4">
        {getHowSteps().map((step) => (
          <div
            key={step.k}
            className="relative flex flex-col rounded-xl border border-border bg-surface p-6 shadow-sm transition-all duration-150 ease-out hover:-translate-y-[1px] hover:shadow-md"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] text-muted-foreground">{step.k}</span>
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {step.chip}
              </span>
            </div>
            <div className="mt-8 text-[16px] font-semibold tracking-tight">{step.t}</div>
            <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{step.d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
// FAQ — design copy (4 questions)
// ──────────────────────────────────────────────────────────────────────────

const getFaqItems = () =>
  [
    {
      q: t`Is this just another ChatGPT wrapper?`,
      a: t`No. We run a fine-tuned pipeline for resume writing, a real ATS keyword engine, and a browser assistant that pre-fills application forms on major job boards. The AI is the last 10%, not the whole thing.`,
    },
    {
      q: t`Will recruiters be able to tell?`,
      a: t`We tailor tone to match your existing voice. Review each draft before sending, or opt in to auto-approve (beta).`,
    },
    {
      q: t`What happens to my data?`,
      a: t`Your resume is yours. Encrypted at rest, never used to train our models. Cancel any time; one click exports everything to JSON and PDF.`,
    },
    {
      q: t`How is this different from Teal or Resume.io?`,
      a: t`Those tools stop at the resume. We also score jobs, tailor per-application, pre-fill forms for your one-click review, and track replies end-to-end.`,
    },
  ] as const;

/**
 * FAQPage structured data, DERIVED from FAQ_ITEMS rather than restated.
 *
 * Google requires the answer text in FAQPage to match what a user actually
 * sees on the page; a schema that drifts from the visible copy is grounds
 * for a manual action. Mapping the same const the <Faq> section renders
 * makes that drift structurally impossible — edit the copy and the schema
 * follows.
 *
 * This is also the block generative engines (ChatGPT / Perplexity / AI
 * Overviews) lift most readily when answering "is Apply Edge a ChatGPT
 * wrapper?" or "what happens to my data?" — questions we already answer
 * better than the sites currently being cited for them.
 */
const getFaqPageLd = () => ({
  "@context": FAQ_SCHEMA.context,
  "@type": FAQ_SCHEMA.pageType,
  mainEntity: getFaqItems().map((item) => ({
    "@type": FAQ_SCHEMA.questionType,
    name: item.q,
    acceptedAnswer: { "@type": FAQ_SCHEMA.answerType, text: item.a },
  })),
});

const Faq = () => (
  <section>
    <div className="mx-auto grid max-w-[1280px] gap-12 px-6 py-24 md:grid-cols-[1fr_1.4fr]">
      <div>
        <div className="text-xs font-medium text-muted-foreground">
          <Trans>FAQ</Trans>
        </div>
        <h2 className="mt-3 text-balance text-[32px] font-semibold leading-[1.05] tracking-[-0.02em] sm:text-[40px]">
          <Trans>Things people ask.</Trans>
        </h2>
        <p className="mt-4 max-w-[32ch] text-[13.5px] text-muted-foreground">
          <Trans>Still on the fence? Start free — a full resume + ATS score with no card.</Trans>
        </p>
      </div>
      <div className="divide-y divide-border border-y border-border">
        {getFaqItems().map((item) => (
          <details key={item.q} className="group py-5 [&[open]>summary>.chev]:rotate-45">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-6">
              <span className="text-[16px] font-medium">{item.q}</span>
              <span className="chev grid size-6 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition-transform duration-300">
                <PlusIcon size={12} />
              </span>
            </summary>
            <p className="mt-3 max-w-[62ch] text-[13.5px] leading-relaxed text-muted-foreground">
              {item.a}
            </p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
// Indigo CTA band
// ──────────────────────────────────────────────────────────────────────────

const Cta = () => {
  const proPrice = getPlan("PRO").price.monthly;
  const ultimatePrice = getPlan("ULTIMATE").price.monthly;

  return (
    <section className="relative overflow-hidden border-t border-border bg-indigo-700 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute inset-0 dot-grid" style={{ filter: "invert(1)" }} />
      </div>
      <div className="relative mx-auto flex max-w-[1280px] flex-col items-start gap-8 px-6 py-20 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-balance text-[36px] font-semibold leading-[1.02] tracking-[-0.02em] sm:text-[44px]">
            <Trans>Tonight: tailor every draft.</Trans>
            <br />
            <span className="font-serif font-normal italic">
              <Trans>Tomorrow: review your queue.</Trans>
            </span>
          </h2>
          <p className="mt-4 max-w-[48ch] text-[14px] opacity-75">
            <Trans>
              Free forever for 2 resumes. ${proPrice}/mo for Pro. ${ultimatePrice}/mo for Ultimate.
            </Trans>
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <a
            href="/auth/register"
            className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-[14px] font-semibold text-indigo-700 hover:bg-white/90"
          >
            <Trans>Start free</Trans> <ArrowRightIcon size={15} />
          </a>
          <div className="font-mono text-[11px] opacity-70">
            <Trans>no card · cancel anytime · 14 day full refund</Trans>
          </div>
        </div>
      </div>
    </section>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Page composition
// ──────────────────────────────────────────────────────────────────────────

export const HomePage = () => (
  <div className="bg-background text-foreground">
    {/*
      Was a bare <Helmet> — which meant the highest-authority page on the site
      shipped no canonical, no Organization/WebSite JSON-LD, no hreflang, and a
      RELATIVE og:image ("/og/default.png") that social crawlers cannot resolve.
      <SEO> supplies all of it, and its defer={false} keeps the head write out
      of requestAnimationFrame so the SSG snapshot reliably captures it.
    */}
    <SEO
      title={t`Apply Edge — Write it once. Send it perfectly.`}
      description={t`Apply Edge writes your resume, tailors each draft to the role you're eyeing, and pre-fills applications across the major job boards.`}
      canonical="/"
      jsonLd={[getFaqPageLd()]}
    />

    <Hero />
    <LogoCloud />
    <FeatureGrid />
    <HowItWorks />
    <CompareSection />
    <Faq />
    <Cta />
  </div>
);
